<script lang="ts">
    import { onMount, onDestroy, tick } from 'svelte';
    import { get } from 'svelte/store';

    import {
        EDIT_ICON,
        FUNNEL_ICON,
        SEND_ICON,
        TIPS_ERROR_ICON,
        VERIFY_SUCCESS_ICON
    } from './constants/loginImage';
    import { getFifaLoginFlowContext } from './context';
    import { MODULE_ENUM } from './enums/loginModule';
    import PhoneChangePopup from './PhoneChangePopup.svelte';
    import {
        changeModule,
        clearCodeInputState,
        codeCountdown,
        codeInputSnapshot,
        codeSendTime,
        isCodeVerifySuccessState,
        isFromPictureModule,
        loginPhoneNumber,
        markCodeSent,
        pageModuleEnterTime,
        persistCodeInputState,
        setLoginFlowLoading,
        startCodeCountdown,
        consumeVerificationCodeLoadingStart,
        suppressCodeModuleLoginFail
    } from './store/loginFlow';
    import Toast from './Toast.svelte';
    import { handleKeyboardActivate } from './utils/a11y';
    import {
        checkPhoneApi,
        getPassportError,
        signInByCodeApi
    } from './utils/loginPassport';
    import {
        fifaLoginSuccessIframe,
        initFifaPreloadIframe
    } from './utils/preloadIframe';
    import { getDurationSince, getErrorTrackAttrs, getModuleDuration } from './utils/trackHelper';

    import webp from '@/actions/webp';

    const {
        bids,
        trackEvent,
        isJumpOneLink,
        normalizePassportLoginData
    } = getFifaLoginFlowContext();

    let toastRefs: Toast | null = null;
    const codeValues = ['', '', '', '', '', ''];
    const codeInputRefs: (HTMLInputElement | null)[] = [
        null,
        null,
        null,
        null,
        null,
        null
    ];
    let codeInput = '';
    const inputTime = [0, 0];
    let showPhonePopup = false;
    let isInputError = false;
    let tipsMessage = '';
    let getCodeTime = 0;
    let codeVerifyLoading = false;
    let isCodeVerifySuccess = false;
    let verifyRequestId = 0;
    let hasReportedCodeInput = false;
    let hasTypAfterSendCode = false;
    let moduleEnterTime = 0;
    let isLoginCompleted = false;

    const reportLoginFail = (
        attrs: Record<string, string | number | boolean | undefined> = {}
    ) => {
        trackEvent(bids.loginFail, attrs);
    };

    $: codeInput = codeValues.join('');

    $: {
        if (codeInput.length === 1) {
            inputTime[0] = window.performance.now();
            hasReportedCodeInput = false;

            if (!hasTypAfterSendCode) {
                trackEvent(bids.verificationCodeTypingTimeSw, {
                    duration: getDurationSince(getCodeTime)
                });
                hasTypAfterSendCode = true;
            }
        }

        if (codeInput.length === 6) {
            inputTime[1] = window.performance.now();

            if (!hasReportedCodeInput) {
                trackEvent(bids.verificationCodeTyp);
                hasReportedCodeInput = true;
            }

            hasTypAfterSendCode = false;

            const wasVerified = get(isCodeVerifySuccessState);
            const savedSnapshot = get(codeInputSnapshot);

            if (!(wasVerified && savedSnapshot === codeInput)) {
                handleVerifyCode();
            }
        }

        handleTipsMessage(false, '');
    }

    const invalidatePendingVerify = () => {
        if (!isCodeVerifySuccess && !codeVerifyLoading) {
            return;
        }

        verifyRequestId += 1;
        isCodeVerifySuccess = false;
        codeVerifyLoading = false;
        setLoginFlowLoading(false);
        clearCodeInputState();
    };

    const handleTipsMessage = (isError: boolean, message: string) => {
        isInputError = isError;
        tipsMessage = message;
    };

    const showToast = (message: string) => {
        toastRefs?.show(message, { type: 'error' });
    };

    const restoreCodeDigits = (digits: string) => {
        const cleanText = digits.replace(/\D/g, '').slice(0, 6);

        for (let i = 0; i < 6; i++) {
            codeValues[i] = cleanText[i] || '';

            if (codeInputRefs[i]) {
                codeInputRefs[i]!.value = codeValues[i];
            }
        }
    };

    const initializeCodeModule = () => {
        verifyRequestId += 1;
        codeVerifyLoading = false;
        isCodeVerifySuccess = false;
        clearCodeInputState();
        handleTipsMessage(false, '');
        handleResetCode();
        handleCodeFocus(0);
    };

    const tryRestoreVerifiedCode = async () => {
        const saved = get(codeInputSnapshot);
        const wasVerified = get(isCodeVerifySuccessState);

        if (!wasVerified || saved.length !== 6) {
            initializeCodeModule();

            return false;
        }

        codeVerifyLoading = false;
        isCodeVerifySuccess = true;
        handleTipsMessage(false, '');
        await tick();
        restoreCodeDigits(saved);

        return true;
    };

    const formatPhoneNumber = (phone: string) => {
        if (phone.length < 10) {
            return phone;
        }

        return [
            phone.slice(0, 2),
            phone.slice(2, 6),
            phone.slice(6)
        ]
            .filter(Boolean)
            .join(' ');
    };

    const handleGetCode = (isInit: boolean) => {
        setLoginFlowLoading(true);
        initializeCodeModule();
        const checkStartTime = window.performance.now();

        checkPhoneApi($loginPhoneNumber)
            .then(() => {
                markCodeSent();
                getCodeTime = window.performance.now();

                if (isInit) {
                    trackEvent(bids.verificationCodeLoadingTimeSw, {
                        duration: getDurationSince(checkStartTime)
                    });
                } else {
                    trackEvent(bids.verificationCodeWaitingTimeSw, {
                        duration: getDurationSince(checkStartTime)
                    });
                    trackEvent(bids.verificationCodeAgainBt, { result: 1 });
                    hasTypAfterSendCode = false;
                }

                startCodeCountdown();
                handleTipsMessage(false, '');
            })
            .catch((err) => {
                const response = getPassportError(err);
                const errorAttrs = getErrorTrackAttrs(response);

                if (!isInit) {
                    trackEvent(bids.verificationCodeAgainBt, {
                        result: 0,
                        ...errorAttrs
                    });
                }

                if (response?.errno === 41002) {
                    changeModule(MODULE_ENUM.PICTURE);
                } else if (response?.errno === 40001) {
                    showToast('Número de celular inválido. Intenta de nuevo.');
                } else if (response?.errno === 53001) {
                    showToast(
                        'Demasiados intentos. Espera un poco antes de volver a intentar.'
                    );
                } else {
                    showToast(
                        'Error al obtener el código de verificación. Inténtalo de nuevo más tarde'
                    );
                }
            })
            .finally(() => {
                setLoginFlowLoading(false);
            });
    };

    const handleVerifyCode = () => {
        const requestId = verifyRequestId;

        isCodeVerifySuccess = false;
        isCodeVerifySuccessState.set(false);
        setLoginFlowLoading(true);
        codeVerifyLoading = true;
        handleCodeBlur();

        signInByCodeApi(codeInput, inputTime[1] - inputTime[0])
            .then((res) => {
                if (requestId !== verifyRequestId) {
                    return;
                }

                const loginData = normalizePassportLoginData(res.response);

                if (!loginData) {
                    handleTipsMessage(
                        true,
                        'Error al iniciar sesión. Inténtalo de nuevo más tarde'
                    );
                    trackEvent(bids.verificationCodePhoneBt, {
                        is_code_bt_success: 0,
                        error_code: 'invalid_response'
                    });
                    trackEvent(bids.verificationCodeBt, {
                        is_jump_success: 0,
                        error_code: 'invalid_response'
                    });
                    reportLoginFail({
                        error_code: 'invalid_response',
                        error_msg: 'invalid_response'
                    });

                    return;
                }

                isCodeVerifySuccess = true;
                persistCodeInputState(codeInput, true);
                isJumpOneLink.set(true);
                isLoginCompleted = true;
                fifaLoginSuccessIframe(res.response);
                trackEvent(bids.verificationCodePhoneBt, {
                    is_code_bt_success: 1
                });
                trackEvent(bids.verificationCodeBt, { is_jump_success: 1 });
                trackEvent(bids.loginSuccess);
                document.dispatchEvent(
                    new CustomEvent('login', {
                        detail: {
                            type: 'success',
                            data: loginData
                        }
                    })
                );
            })
            .catch((err) => {
                if (requestId !== verifyRequestId) {
                    return;
                }

                isCodeVerifySuccess = false;
                persistCodeInputState(codeInput, false);
                const response = getPassportError(err);
                const errorAttrs = getErrorTrackAttrs(response);

                trackEvent(bids.verificationCodePhoneBt, {
                    is_code_bt_success: 0,
                    ...errorAttrs
                });
                trackEvent(bids.verificationCodeBt, {
                    is_jump_success: 0,
                    ...errorAttrs
                });
                reportLoginFail(errorAttrs);
                if (bids.loginRisk) {
                    trackEvent(bids.loginRisk, { is_sign_success: 0 });
                }

                if (response?.errno === 53001) {
                    handleTipsMessage(
                        true,
                        'Demasiados intentos. Espera un poco antes de volver a intentar.'
                    );
                } else if (response?.errno === 40002) {
                    handleTipsMessage(
                        true,
                        'El código de verificación no es válido. Por favor, inténtalo de nuevo'
                    );
                } else if (response?.errno === 40003) {
                    handleTipsMessage(
                        true,
                        'El código de verificación venció. Haz clic en el botón y obtén otro'
                    );
                } else {
                    handleTipsMessage(
                        true,
                        'Error al iniciar sesión por un riesgo detectado con el núm. de celular'
                    );
                }
            })
            .finally(() => {
                if (requestId !== verifyRequestId) {
                    return;
                }

                codeVerifyLoading = false;
                setLoginFlowLoading(false);
            });
    };

    const handleResetCode = () => {
        for (let i = 0; i < 6; i++) {
            codeValues[i] = '';

            if (codeInputRefs[i]) {
                codeInputRefs[i]!.value = '';
            }
        }
    };

    const handleCodePaste = (event: ClipboardEvent) => {
        event.preventDefault();
        invalidatePendingVerify();
        const pastedText = event.clipboardData?.getData('text') || '';
        const cleanText = pastedText.replace(/[^0-9]/g, '').slice(0, 6);

        for (let i = 0; i < 6; i++) {
            codeValues[i] = cleanText[i] || '';

            if (codeInputRefs[i]) {
                codeInputRefs[i]!.value = codeValues[i];
            }
        }

        codeInputRefs[Math.min(cleanText.length, 5)]?.focus();
    };

    const handleCodeInputClick = (index: number, event: Event) => {
        trackEvent(bids.verificationCodeCk, { button_name: 'input_box' });
        handleCodeFocus(index, event);
    };

    const handleCodeFocus = (index: number, event?: Event) => {
        const hasAnyInput = codeValues.some((value) => value !== '');
        const targetIndex = !hasAnyInput ? 0 : index;
        const input =
            targetIndex === index && event
                ? (event.currentTarget as HTMLInputElement)
                : codeInputRefs[targetIndex];

        if (!input) {
            return;
        }

        const length = input.value.length;

        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(length, length);
        });
    };

    const handleCodeBlur = () => {
        codeInputRefs.forEach((input) => {
            input?.blur();
        });
    };

    const handleCodeInput = (index: number, event: Event) => {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/[^0-9]/g, '').slice(0, 1);

        invalidatePendingVerify();

        codeValues[index] = value;
        input.value = value;

        if (value && index < 5) {
            codeInputRefs[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, event: KeyboardEvent) => {
        if (event.key !== 'Backspace') {
            return;
        }

        if (codeValues[index]) {
            invalidatePendingVerify();
            codeValues[index] = '';

            if (codeInputRefs[index]) {
                codeInputRefs[index]!.value = '';
            }

            return;
        }

        if (index > 0) {
            codeInputRefs[index - 1]?.focus();
        }
    };

    onMount(async () => {
        initFifaPreloadIframe(bids);

        const enterTime = window.performance.now();
        const loadingStartTime = consumeVerificationCodeLoadingStart();

        moduleEnterTime = enterTime;
        pageModuleEnterTime.set(enterTime);
        getCodeTime = get(codeSendTime) || enterTime;

        if (!$isFromPictureModule && loadingStartTime !== null) {
            trackEvent(bids.verificationCodeLoadingTimeSw, {
                duration: getModuleDuration(loadingStartTime)
            });
        }

        trackEvent(bids.verificationCodeSw);

        const restored = await tryRestoreVerifiedCode();

        if ($isFromPictureModule) {
            handleGetCode(true);

            return;
        }

        if (!restored) {
            handleCodeFocus(0);
        }
    });

    onDestroy(() => {
        hasTypAfterSendCode = false;

        if (!isLoginCompleted && !get(suppressCodeModuleLoginFail)) {
            reportLoginFail({
                error_code: 'user_back',
                error_msg: 'verification_code_page_exit'
            });
        }

        suppressCodeModuleLoginFail.set(false);
        trackEvent(bids.verificationCodeEx);
        trackEvent(bids.verificationCodeTimeSw, {
            duration: getModuleDuration(moduleEnterTime)
        });
    });

    const openPhonePopup = () => {
        trackEvent(bids.verificationCodeCk, { button_name: 'change_phone' });
        showPhonePopup = true;
    };

    const requestNewCode = () => {
        trackEvent(bids.verificationCodeCk, {
            button_name: 'get_verification_code_again'
        });
        handleGetCode(false);
    };

</script>

<div class="code-module">
    <div class="title">
        ¡Ya casi!<br />
        Ahora ingresa tu código de 6 dígitos
    </div>
    <div class="phone-wrapper">
        <div class="phone-number">{formatPhoneNumber($loginPhoneNumber)}</div>
        <div
            class="change-phone-number"
            role="button"
            tabindex="0"
            on:click={openPhonePopup}
            on:keydown={(event) => handleKeyboardActivate(event, openPhonePopup)}
        >
            <div class="change-phone-number-text">Corregir mi número</div>
            <div class="change-phone-number-icon" use:webp={EDIT_ICON}></div>
        </div>
    </div>
    <div class="input-tips">Escribe o pega el código de 6 números</div>
    <div
        class="input-wrapper"
        class:is-input-error={isInputError}
        on:paste={handleCodePaste}
    >
        {#each codeValues as _, index}
            <input
                type="tel"
                class="code-input"
                class:is-input={codeValues[index]}
                maxlength="1"
                bind:this={codeInputRefs[index]}
                value={codeValues[index]}
                on:focus={(event) => handleCodeFocus(index, event)}
                on:click={(event) => handleCodeInputClick(index, event)}
                on:input={(event) => handleCodeInput(index, event)}
                on:keydown={(event) => handleCodeKeyDown(index, event)}
            />
        {/each}
    </div>
    <div class="retry-wrapper">
        <div class="show-message-wrapper">
            {#if tipsMessage}
                <div class="show-message-text">
                    <div class="show-message-icon" use:webp={TIPS_ERROR_ICON}></div>
                    <span>{tipsMessage}</span>
                </div>
            {/if}
            <div class="verify-message-wrapper">
                {#if isCodeVerifySuccess}
                    <div class="verify-message-icon" use:webp={VERIFY_SUCCESS_ICON}></div>
                    <span class="verify-message-text">Código verificado</span>
                {:else if codeVerifyLoading}
                    <div class="verify-message-icon" use:webp={FUNNEL_ICON}></div>
                    <span class="verify-message-text">Confirmando tu código...</span>
                {:else if $codeCountdown > 0}
                    <div class="verify-message-icon" use:webp={FUNNEL_ICON}></div>
                    <span class="verify-message-text">
                        Espera <span>{$codeCountdown} segundos</span> para un nuevo código
                    </span>
                {:else}
                    <div
                        class="send-message-text"
                        role="button"
                        tabindex="0"
                        on:click={requestNewCode}
                        on:keydown={(event) =>
                            handleKeyboardActivate(event, requestNewCode)}
                    >
                        Obtener un nuevo código
                    </div>
                    <div
                        class="send-message-icon"
                        role="button"
                        tabindex="0"
                        on:click={requestNewCode}
                        on:keydown={(event) =>
                            handleKeyboardActivate(event, requestNewCode)}
                        use:webp={SEND_ICON}
                    ></div>
                {/if}
            </div>
        </div>
        <div class="input-length-show" class:is-input-complete={codeInput.length === 6}>
            <span>{codeInput.length}</span>/6
        </div>
    </div>
</div>

<Toast bind:this={toastRefs} />

<PhoneChangePopup
    isOpen={showPhonePopup}
    on:close={() => {
        showPhonePopup = false;
    }}
    on:confirm={() => {
        showPhonePopup = false;
        getCodeTime = get(codeSendTime) || window.performance.now();
        hasTypAfterSendCode = false;
        initializeCodeModule();
    }}
/>

<style lang="scss">
    .code-module {
        padding: 0 0.4rem;

        .title {
            padding: 0.4rem 0 0.6rem;
            font-family: DiDiSans-Pro-ExtraBold;
            font-size: 0.48rem;
            line-height: 0.64rem;
            color: #0b0b0d;
        }

        .phone-number {
            font-family: DiDiSans-Pro-Bold;
            font-size: 0.4rem;
            line-height: 0.56rem;
            color: #0c0c0d;
            margin-bottom: 0.08rem;
        }

        .change-phone-number {
            display: flex;
            align-items: center;
            margin-bottom: 0.6rem;
            cursor: pointer;
        }

        .change-phone-number-text {
            font-size: 0.32rem;
            font-family: DiDiSans-Pro-Medium;
            line-height: 0.4rem;
            color: #3355ff;
        }

        .change-phone-number-icon {
            width: 0.32rem;
            height: 0.32rem;
            background-size: 100% 100%;
            margin-left: 0.08rem;
        }

        .input-tips {
            font-size: 0.32rem;
            color: #8a8a91;
            margin-bottom: 0.32rem;
        }

        .input-wrapper {
            display: flex;
            justify-content: center;
            gap: 0.14rem;
            margin-bottom: 0.22rem;

            .code-input {
                width: 1rem;
                height: 1rem;
                border: none;
                border-bottom: 0.02rem solid #bababf;
                border-radius: 0;
                -webkit-appearance: none;
                appearance: none;
                text-align: center;
                font-family: DiDiSans-Pro-Bold;
                font-size: 0.6rem;
                line-height: 0.76rem;
                color: #0c0c0d;
                background: #ffffff;
                outline: none;

                &:focus {
                    border-color: #0c0c0d;
                    caret-color: #0c0c0d;
                }
            }

            .is-input {
                border-color: #0c0c0d;
            }

            &.is-input-error .code-input {
                border-color: #f2183d;
                color: #f2183d;
            }
        }

        .retry-wrapper {
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        .show-message-wrapper {
            flex: 1;
            min-width: 0;
            margin-right: 0.75rem;
        }

        .show-message-text {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.32rem;
            font-size: 0.24rem;
            color: #f2183d;

            span {
                flex: 1;
                line-height: 0.32rem;
                word-break: break-word;
            }
        }

        .show-message-icon {
            flex-shrink: 0;
            width: 0.28rem;
            height: 0.28rem;
            background-size: 100% 100%;
            margin-right: 0.07rem;
        }

        .verify-message-wrapper {
            display: flex;
            align-items: center;
        }

        .verify-message-icon {
            width: 0.28rem;
            height: 0.28rem;
            background-size: 100% 100%;
            margin-right: 0.07rem;
        }

        .verify-message-text {
            font-size: 0.24rem;
            color: #0c0c0d;

            span {
                color: #3355ff;
            }
        }

        .send-message-text {
            font-size: 0.32rem;
            color: #3355ff;
            cursor: pointer;
        }

        .send-message-icon {
            width: 0.32rem;
            height: 0.32rem;
            background-size: 100% 100%;
            margin-left: 0.07rem;
            cursor: pointer;
        }

        .input-length-show {
            position: absolute;
            right: 0;
            top: 0;
            font-size: 0.28rem;
            color: #0b0b0d;

            span {
                color: #bababf;
            }

            &.is-input-complete span {
                color: #0b0b0d;
            }
        }
    }
</style>
